import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  VStack,
  Spinner,
  useToast,
  useColorModeValue,
  Image,
  Button,
} from '@chakra-ui/react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FBPurchaseDrawer from '../components/FBPurchaseDrawer';
import SplitText from '../components/reactBits/SplitText';

const FB = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [donationAmount, setDonationAmount] = useState(0);
  const [donationItemName, setDonationItemName] = useState('');
  const [isFBPurchaseDrawerOpen, setIsFBPurchaseDrawerOpen] = useState(false);
  const [monthlyConsumption, setMonthlyConsumption] = useState({
    bevande: 0,
    snack: 0,
    cibo: 0,
    total: 0,
  });
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Funzione per ottenere l'immagine del prodotto dal CMS
  const getProductImage = (stock) => {
    if (stock.thumbnail && stock.thumbnail.url) {
      return stock.thumbnail.url;
    }
    return null;
  };

  // Carica le statistiche dei consumi mensili
  const fetchMonthlyConsumption = async () => {
    try {
      const response = await axios.get('/api/statistics');
      if (response.data.monthlyFBConsumption) {
        setMonthlyConsumption(response.data.monthlyFBConsumption);
      }
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche mensili:', error);
    }
  };

  // Carica gli stock disponibili
  const fetchStocks = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/stocks');
      const stocksData = response.data.data || [];

      console.log('📊 FB.js - Stock Data Ricevuti:', {
        totalReceived: stocksData.length,
        rawData: stocksData.map((stock) => ({
          Nome: stock.Nome,
          tipo: stock.tipo,
          quantita: stock.quantita,
          prezzoVendita: stock.prezzoVendita,
          disponibili: stock.disponibili,
        })),
      });

      // Filtra ulteriormente per assicurarsi che solo prodotti con unità disponibili > 0 siano mostrati
      // Mostra solo Snack e Bevande
      // Filtra ulteriormente per assicurarsi che solo prodotti con unità disponibili > 0 siano mostrati
      // Mostra solo Snack e Bevande
      const availableStocks = stocksData.filter(
        (stock) =>
          stock.disponibili &&
          stock.disponibili > 0 &&
          stock.prezzoVendita &&
          stock.prezzoVendita > 0 &&
          (stock.tipo === 'Snack' || stock.tipo === 'Bevande')
      );

      // Raggruppa per nome e tipo per la visualizzazione (evita duplicati)
      const groupedStocks = {};
      availableStocks.forEach((stock) => {
        const key = `${stock.Nome}_${stock.tipo}`;
        if (!groupedStocks[key]) {
          groupedStocks[key] = {
            ...stock,
            quantita: 0,
            disponibili: 0,
          };
        }
        groupedStocks[key].quantita += stock.quantita || 0;
        groupedStocks[key].disponibili += stock.disponibili || 0;
      });

      const finalStocks = Object.values(groupedStocks);

      console.log('✅ FB.js - Stock Disponibili (Dopo Filtro):', {
        totalAvailable: finalStocks.length,
        availableProducts: finalStocks.map((stock) => ({
          Nome: stock.Nome,
          tipo: stock.tipo,
          quantita: stock.quantita, // Quantità totale acquistata
          vendute: stock.vendute, // Unità vendute
          disponibili: stock.disponibili, // Unità disponibili (quantita - vendute)
          prezzoVendita: stock.prezzoVendita,
          status:
            stock.disponibili < 5
              ? '🔴 Stock molto basso'
              : stock.disponibili < 10
                ? '🟠 Stock basso'
                : '🟢 Stock normale',
        })),
      });

      // Log per tipo
      const byType = {};
      finalStocks.forEach((stock) => {
        if (!byType[stock.tipo]) {
          byType[stock.tipo] = [];
        }
        byType[stock.tipo].push(stock.Nome);
      });

      console.log('📋 FB.js - Disponibilità per Tipo:', byType);

      setStocks(finalStocks);
    } catch (error) {
      console.error('❌ FB.js - Errore nel caricamento degli stock:', error);
      toast({
        title: 'Errore',
        description: 'Errore nel caricamento dei prodotti disponibili',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
    fetchMonthlyConsumption();
  }, []);

  const handlePurchase = (stock) => {
    console.log('🛒 FB.js - Tentativo Acquisto:', {
      prodotto: stock.Nome,
      tipo: stock.tipo,
      quantita: stock.quantita, // Quantità totale acquistata
      vendute: stock.vendute, // Unità vendute
      disponibili: stock.disponibili, // Unità disponibili (quantita - vendute)
      prezzoVendita: stock.prezzoVendita,
      isAuthenticated,
      timestamp: new Date().toISOString(),
    });

    if (!isAuthenticated) {
      console.log('❌ FB.js - Acquisto Bloccato: Utente non autenticato');
      toast({
        title: 'Login Richiesto',
        description: 'Devi effettuare il login per acquistare prodotti',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Verifica che il prodotto sia disponibile
    if (!stock.disponibili || stock.disponibili <= 0) {
      console.log('❌ FB.js - Acquisto Bloccato: Prodotto non disponibile', {
        prodotto: stock.Nome,
        disponibili: stock.disponibili,
        quantita: stock.quantita,
        vendute: stock.vendute,
      });
      toast({
        title: 'Prodotto Non Disponibile',
        description: `${stock.Nome} non è attualmente disponibile`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    console.log('✅ FB.js - Acquisto Autorizzato:', {
      prodotto: stock.Nome,
      disponibili: stock.disponibili,
      prezzo: stock.prezzoVendita,
    });

    // Imposta l'importo e il nome dell'item, poi apri il drawer F&B
    setDonationAmount(stock.prezzoVendita);
    setDonationItemName(stock.Nome);
    setIsFBPurchaseDrawerOpen(true);
  };

  const closeFBPurchaseDrawer = () => {
    console.log('🔄 FB.js - Chiusura Drawer e Refresh Lista');
    setIsFBPurchaseDrawerOpen(false);
    setDonationAmount(0);
    setDonationItemName('');

    // Ricarica la lista per assicurarsi che sia aggiornata
    fetchStocks();
    fetchMonthlyConsumption();
  };

  const groupStocksByType = (stocksList) => {
    const grouped = {};
    stocksList.forEach((stock) => {
      if (!grouped[stock.tipo]) {
        grouped[stock.tipo] = [];
      }
      grouped[stock.tipo].push(stock);
    });

    // Ordina i tipi: Bevande prima, poi Snack (solo questi due tipi)
    const typeOrder = ['Bevande', 'Snack'];
    const orderedGrouped = {};
    typeOrder.forEach((type) => {
      if (grouped[type]) {
        orderedGrouped[type] = grouped[type];
      }
    });

    return orderedGrouped;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" />
        <Text mt={4}>Caricamento prodotti...</Text>
      </Box>
    );
  }

  return (
    <Box maxW="6xl" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Descrizione iniziale */}
        <Box
          textAlign="center"
          py={4}
          px={6}
          bg="purple.50"
          borderRadius="lg"
          border="1px"
          borderColor="purple.200"
        >
          <Text
            fontSize="md"
            color="purple.700"
            fontWeight="medium"
            lineHeight="1.6"
          >
            💜 <strong>Sostieni CIL con ogni acquisto!</strong>
            <br />
            Ogni bevanda e snack che acquisti contribuisce direttamente al
            sostegno dell&apos;associazione e alle nostre attività.
          </Text>
        </Box>

        {/* Counter consumi mensili */}
        <Box
          textAlign="center"
          py={3}
          px={6}
          bg="gray.50"
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
        >
          <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={2}>
            📊 Consumi di questo mese
          </Text>
          <SimpleGrid columns={3} spacing={4}>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                {monthlyConsumption.bevande}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Bevande
              </Text>
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="orange.600">
                {monthlyConsumption.snack}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Snack
              </Text>
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="green.600">
                {monthlyConsumption.cibo}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Cibo
              </Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Prodotti Disponibili */}
        {stocks.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color="gray.500" fontSize="lg">
              Nessun prodotto disponibile al momento
            </Text>
          </Box>
        ) : (
          <VStack spacing={8} align="stretch">
            {Object.entries(groupStocksByType(stocks)).map(
              ([tipo, stocksOfType]) => (
                <Box key={tipo}>
                  <Heading size="lg" mb={4} color="gray.700" textAlign="center">
                    {tipo}
                  </Heading>
                  <SimpleGrid columns={{ base: 3, md: 4, lg: 5 }} spacing={4}>
                    {stocksOfType.map((stock) => (
                      <Card
                        key={stock.documentId}
                        bg={cardBg}
                        borderColor={borderColor}
                        cursor={isAuthenticated ? 'pointer' : 'not-allowed'}
                        onClick={() => isAuthenticated && handlePurchase(stock)}
                        _hover={
                          isAuthenticated
                            ? {
                                shadow: 'md',
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s',
                              }
                            : {}
                        }
                        opacity={isAuthenticated ? 1 : 0.6}
                      >
                        <CardBody p={3}>
                          <VStack spacing={3} align="stretch">
                            <Box textAlign="center">
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="gray.700"
                                lineHeight="1.2"
                              >
                                {stock.Nome}
                              </Text>
                            </Box>

                            <Box textAlign="center">
                              <Image
                                src={getProductImage(stock) || '/logo192.png'}
                                alt={stock.Nome}
                                maxH="120px"
                                objectFit="contain"
                                mx="auto"
                                fallbackSrc="/logo192.png"
                              />
                            </Box>

                            <Box textAlign="center">
                              <Text
                                fontSize="lg"
                                fontWeight="bold"
                                color="red.600"
                              >
                                €{stock.prezzoVendita.toFixed(2)}
                              </Text>
                            </Box>

                            <Box textAlign="center">
                              <Text fontSize="xs" color="gray.500">
                                {isAuthenticated
                                  ? 'Tocca per acquistare'
                                  : 'Login richiesto'}
                              </Text>
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>

                  {/* CTA Patron tra Bevande e Snack */}
                  {tipo === 'Bevande' && (
                    <Box textAlign="center" py={6}>
                      <Box
                        fontSize="2xl"
                        fontWeight="bold"
                        color="purple.600"
                        mb={2}
                      >
                        <SplitText
                          text="Diventa un Patron!"
                          tag="span"
                          className="patron-title"
                          splitType="chars"
                          delay={50}
                          duration={0.8}
                          from={{ opacity: 0, y: 30 }}
                          to={{ opacity: 1, y: 0 }}
                          threshold={0.2}
                        />
                      </Box>
                      <Text fontSize="md" color="gray.600" mb={4}>
                        Supporta la nostra community e ottieni vantaggi
                        esclusivi
                      </Text>
                      <Button
                        colorScheme="purple"
                        size="lg"
                        onClick={() => navigate('/patreon')}
                      >
                        Diventa Patron
                      </Button>
                    </Box>
                  )}
                </Box>
              )
            )}
          </VStack>
        )}

        {/* F&B Purchase Drawer */}
        <FBPurchaseDrawer
          isOpen={isFBPurchaseDrawerOpen}
          onClose={closeFBPurchaseDrawer}
          stockItem={donationItemName}
          amount={donationAmount}
        />
      </VStack>
    </Box>
  );
};

export default FB;
